import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import classNames from 'classnames';
import {
  Button,
  ChevronDownIcon,
  Divider,
  DropdownItem,
  FileDragDropIcon,
  Grid,
  GridItem,
  HelpIcon,
  IconButton,
  Select,
  SensitiveInfoIcon,
  Tab,
  TabBox,
  TabPanel,
  Tabs,
  TextField,
  ToolTip,
  Typography,
  useAlert,
} from 'fasoo-ui-component-next';
import _ from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  DetectedFileInfo,
  DetectedInfo,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import exchangeOfflineApprovalApi, {
  ExchangeOfflineApprovalDetailEntity,
} from 'src/api/user/exchangeOfflineApprovalApi';
import fileDownloadApi from 'src/api/user/fileDownloadApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import i18n, { getFasooUiComponentLanguage } from 'src/i18n';
import config from 'src/shared/config';
import { toLocalDateLangStr, toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';
import { getMobileDownloadOptions } from 'src/shared/utils/sessionStorageManager';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import { openMobileViewer } from './openApp';
import RemarkPopup from './RemarkPopup';
import {
  ApprovalDetailInfoPageType,
  ApprovalUserInfoDto,
  DETECTED_STATUS_LIST,
  DownloadHistoryInfo,
  FileIcon,
  ShowLineContent,
  UserDetail,
} from '.';

import './ExchangeOfflineDetail.scss';

const FXM_API_URL = config.servers.fxm.url;
const mobileDownloadOptions = getMobileDownloadOptions();

export interface ExchangeOfflineApprovalDetailProps extends CommonProps {
  exchangeOfflineDetail: ExchangeOfflineApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  pageType?: ApprovalDetailInfoPageType;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

export const ExchangeOfflineDetail: React.FC<ExchangeOfflineApprovalDetailProps> = ({
  exchangeOfflineDetail,
  ResetDetail,
  onCloseDetail,
  cancelApprovalChildren,
  pageType = 'others',
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval']);
  const { alert } = useAlert();
  const [tab, setTab] = useState(0);

  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();

  // 개인 정보 검출
  const [detectInput, setDetectedInput] = useState<string>('');
  const [selectedDetectFile, setSelectedDetectFile] = useState<string | null>('');
  const [detectedSearchList, setDetectedSearchList] = useState<DetectedFileInfo[]>([]);

  if (exchangeOfflineDetail === null || exchangeOfflineDetail === undefined) {
    return null;
  }

  let approvers: ApprovalUserInfoDto[] = [];
  if (exchangeOfflineDetail.completeApprovers.length > 0) {
    const completes = _.sortBy(exchangeOfflineDetail.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (exchangeOfflineDetail.progressApprovers.length > 0) {
    const progresses = _.sortBy(exchangeOfflineDetail.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }

  approvers = approvers.concat(
    exchangeOfflineDetail.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );
  // }

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (exchangeOfflineDetail.progressApprovers.length > 0) {
      const progressApproverInfo = exchangeOfflineDetail.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: exchangeOfflineDetail.approvalNo,
        approvalMethod: exchangeOfflineDetail.approvalMethod,
        approvalType: exchangeOfflineDetail.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then((resp) => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  const handleChangeDetectedInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDetectedInput(e.target.value);
    debouncedHandleChangeSearchText(e.target.value);
  };

  const handleSelectInput = (eventKey: string): void => {
    if (!exchangeOfflineDetail.detectedFiles) {
      return;
    }
    exchangeOfflineDetail.detectedFiles.forEach((element) => {
      if (element.fileUuid === eventKey) {
        setDetectedInput(element.fileName);
        setSelectedDetectFile(eventKey);
      }
    });
  };

  const debouncedHandleChangeSearchText = _.debounce((str: string) => {
    if (!exchangeOfflineDetail.detectedFiles) {
      return;
    }
    if (str.length > 0) {
      const res = exchangeOfflineDetail.detectedFiles.filter((item) => item.fileName.includes(str));
      setDetectedSearchList(res);
    } else {
      setDetectedSearchList(exchangeOfflineDetail.detectedFiles);
    }
  }, 300);

  const detectedFilesLen = (type: 'pattern' | 'sensitive'): number => {
    if (!exchangeOfflineDetail || !exchangeOfflineDetail.detectedFiles) {
      return 0;
    }
    const fileInfo = exchangeOfflineDetail.detectedFiles.find(
      (file) => file.fileUuid === selectedDetectFile
    );
    if (!fileInfo) {
      return 0;
    }
    if (type === 'pattern') {
      return fileInfo.totalPatternCount;
    }
    if (type === 'sensitive') {
      return fileInfo.totalPatternDetailCount;
    }
    return 0;
  };

  const ViewRight = (): JSX.Element => {
    const filterdRights = [exchangeOfflineDetail.rightView, exchangeOfflineDetail.rightPrint]
      .map((right, idx) => {
        const rights = ['view', 'print'];
        return right ? t(`userRequest:receiverRight.rights.${rights[idx]}`) : null;
      })
      .filter((right) => right);
    return (
      <div className="mg mt-6">
        {filterdRights.map((right, idx) => (idx > 0 ? ` · ${right}` : right))}
      </div>
    );
  };

  // 파일 다운로드(fasoo view, fasoo view+, secure view)
  const handleFileDownload = (approvalNo: string, fileUuid: string): void => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(userAgent);
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    if (isAndroid || isIOS) {
      fileDownloadApi
        .prepareDownload(approvalNo, fileUuid)
        .then(({ uuid }) => {
          const downloadUrl = encodeURIComponent(
            `${document.location.origin}${`${FXM_API_URL}/api/download/moriginal?uuid=${uuid}`}`
          );
          openMobileViewer(downloadUrl, getFasooUiComponentLanguage());
        })
        .catch((err) => {
          console.log('err', err);
        });
    } else {
      console.log('Is not mobile (Android, IOS)');
    }
  };

  return (
    <div className="exchange-offline-detail-root">
      <div
        className={classNames('exchange-offline-detail-maincontainer', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        {/* 신청 유형 아이콘 area */}

        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={exchangeOfflineDetail.approvalType}
          status={exchangeOfflineDetail.status}
        />
        <TabBox tabWidth="full" line classes="exchange-offline-detail-tab">
          <Tabs
            value={tab}
            onChange={(selected) => {
              setTab(selected);
              if (selected === 1) {
                ResetDetail();
              }
            }}
          >
            <Tab label={t('userApproval:tab.basicInfo')} index={0} />
            <Tab label={t('userApproval:tab.usageLogs')} index={1} />
          </Tabs>
          <TabPanel value={tab} index={0}>
            {!exchangeOfflineDetail.fileDetails || (
              <ShowLineContent title={t('userApproval:fileDetails.$')}>
                <Grid spacing={0}>
                  <GridItem xs={12} padding="24px 0px 0px 0px">
                    <Grid spacing={0}>
                      <GridItem
                        classes={classNames('exchange-offline-file-title')}
                        xs={6}
                        alignSelf="center"
                      >
                        <Typography variant="h3">{t('userApproval:fileDetails.$')}</Typography>
                        <span className="file-count">
                          {' '}
                          {exchangeOfflineDetail.fileDetails.length}
                        </span>
                      </GridItem>
                    </Grid>
                  </GridItem>
                  <GridItem xs={12} classes={classNames('files-overflow')}>
                    {exchangeOfflineDetail.fileDetails.map((fileDetail, fileIdx) => (
                      <div
                        key={`file-${fileIdx}`}
                        className="pt-10 flex"
                        onClick={() => {
                          if (mobileDownloadOptions.useDownload) {
                            handleFileDownload(fileDetail.approvalNo, fileDetail.fileUuid);
                          }
                        }}
                        onTouchStart={() => {
                          if (mobileDownloadOptions.useDownload) {
                            handleFileDownload(fileDetail.approvalNo, fileDetail.fileUuid);
                          }
                        }}
                      >
                        {FileIcon(fileDetail.fileName)}
                        <div className="download-file-name">{fileDetail.fileName}</div>
                      </div>
                    ))}
                  </GridItem>
                </Grid>
              </ShowLineContent>
            )}
            {!exchangeOfflineDetail.approvalMethod || (
              <ShowLineContent
                classes={classNames('approval-method')}
                title={t('userApproval:approvalMethod.$')}
                content={t(`userApproval:approvalMethod.${exchangeOfflineDetail.approvalMethod}`)}
              />
            )}
            <ShowLineContent
              title={t('userApproval:applicant')}
              content={
                <UserDetail
                  type="request"
                  dateStr={toLocalDateTimeLangStr(
                    exchangeOfflineDetail.applyDate,
                    getFasooUiComponentLanguage()
                  )}
                  remark={exchangeOfflineDetail.remark}
                  userInfo={{
                    userId: exchangeOfflineDetail.userId,
                    userName: exchangeOfflineDetail.userName,
                    positionName: exchangeOfflineDetail.positionName,
                    deptName: exchangeOfflineDetail.deptName,
                    actorIp: exchangeOfflineDetail.actorIp,
                  }}
                  submission={exchangeOfflineDetail.submission}
                />
              }
            />
            {exchangeOfflineDetail.detectedFiles && exchangeOfflineDetail.detectedFiles.length > 0 && (
              <ShowLineContent
                title={
                  <>
                    <div className="flex align-center">{t('userApproval:secDataDetection')}</div>
                    <div className="sec-description">
                      {DETECTED_STATUS_LIST.fst.map((element) => (
                        <div className="detect-description-element">
                          <div className="detect-status">
                            <span className={`detect-status-${element}`} />
                          </div>
                          {t(`userApproval:sensitiveData.status.${element}`)}
                        </div>
                      ))}
                    </div>
                    <div className="sec-description">
                      {DETECTED_STATUS_LIST.snd.map((element) => (
                        <div className="detect-description-element">
                          <div className="detect-status">
                            <span className={`detect-status-${element}`} />
                          </div>
                          {t(`userApproval:sensitiveData.status.${element}`)}
                          {element === 'ERROR' && (
                            <ToolTip
                              classes="mg ml-4"
                              as="div"
                              message={
                                <span className="fs-12">
                                  {t(`userApproval:sensitiveData.status.ERRORHelp`)}
                                </span>
                              }
                              horiozionPosition="center"
                            >
                              <HelpIcon />
                            </ToolTip>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                }
                content={
                  <>
                    <Select
                      classes={classNames('sec-select-root')}
                      onClick={handleSelectInput}
                      selectedEventKey={selectedDetectFile || 'default-key'}
                      size="mds"
                      // width={260}
                      isTextInput
                      // key={}
                      fullWidth
                      textInput={
                        <TextField
                          value={detectInput}
                          onChange={handleChangeDetectedInput}
                          size="sm"
                          placeholder={t('userApproval:sensitiveData.placeholder')}
                          endIcon={
                            <IconButton>
                              <ChevronDownIcon />
                            </IconButton>
                          }
                        />
                      }
                    >
                      {detectedSearchList.length === 0
                        ? exchangeOfflineDetail.detectedFiles.map((file) => {
                            return (
                              <DropdownItem
                                severity={file.detectStatus === 'DETECTED' ? undefined : 'disabled'}
                                eventKey={file.fileUuid}
                              >
                                <div className="flex">
                                  <div className="detect-status">
                                    <span className={`detect-status-${file.detectStatus}`} />
                                  </div>

                                  {file.fileName}
                                </div>
                              </DropdownItem>
                            );
                          })
                        : detectedSearchList.map((file) => {
                            return (
                              <DropdownItem
                                severity={file.detectStatus === 'DETECTED' ? undefined : 'disabled'}
                                eventKey={file.fileUuid}
                              >
                                <div className="flex">
                                  <div className="detect-status">
                                    <span className={`detect-status-${file.detectStatus}`} />
                                  </div>

                                  {file.fileName}
                                </div>
                              </DropdownItem>
                            );
                          })}
                      {/* {selectedDetectFile === null && (
                    <DropdownItem eventKey="default-key" classes="dummy-dropdown">
                      <Typography variant="p" color="description">
                        {'문서를 선택해 주세요'}
                      </Typography>
                    </DropdownItem>
                  )} */}
                    </Select>
                    {selectedDetectFile !== null && (
                      <Typography variant="p" color="secondary" classes="mg mt-12">
                        <Trans i18nKey="userApproval:totalPattern">
                          <span />
                          <span />
                          <span className="file-count">{{ n: detectedFilesLen('pattern') }}</span>
                          <span />
                        </Trans>
                      </Typography>
                    )}
                    {selectedDetectFile !== null && (
                      <Typography variant="p" color="secondary" classes="mg mb-8">
                        <Trans i18nKey="userApproval:totalPatternInfoCount">
                          <span />
                          <span />
                          <span className="file-count">{{ n: detectedFilesLen('sensitive') }}</span>
                          <span />
                        </Trans>
                      </Typography>
                    )}
                    {exchangeOfflineDetail.detectedFiles
                      .find((file) => file.fileUuid === selectedDetectFile)
                      ?.detectedInfos.map((info) => (
                        <div key={info.patternId} className={classNames('sensitive-info-row')}>
                          <SensitiveInfoIcon />
                          <div className="sensitive-info-row-Item">
                            <div>{info.patternName}</div>
                            <div className="sensitive-info-row-count">{info.detectedCount}</div>
                          </div>
                        </div>
                      ))}
                  </>
                }
              />
            )}
            {/* 메일 수신자 권한 */}
            <div className="approval-detail-info-title">
              <div className="title-text">{t('userApproval:mailReciverRight')}</div>
            </div>
            <ShowLineContent
              title=""
              content={
                <>
                  <Divider />
                  <ShowLineContent
                    subTitle={t('userApproval:mailReciverRight')}
                    content={<ViewRight />}
                  />
                  <ShowLineContent
                    subTitle={t('userApproval:viewPeriod')}
                    content={
                      <>
                        {toLocalDateLangStr(
                          exchangeOfflineDetail.viewStartTs,
                          getFasooUiComponentLanguage()
                        )}
                        {' ~ '}
                        {toLocalDateLangStr(
                          exchangeOfflineDetail.viewEndTs,
                          getFasooUiComponentLanguage()
                        )}
                      </>
                    }
                  />
                  <Divider classes="mg mt-12" />
                </>
              }
            />
            {/* 결재 상세 정보 */}
            <div className="approval-detail-info-title">
              <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
            </div>
            <ShowLineContent
              title=""
              content={
                <>
                  {approvers.map((approver, idx) => {
                    return (
                      <UserDetail
                        step={approvers.length > 1 ? approver.step : undefined}
                        type="approver"
                        dateStr={
                          approver.approvalDate
                            ? toLocalDateTimeLangStr(
                                approver.approvalDate,
                                getFasooUiComponentLanguage()
                              )
                            : null
                        }
                        remark={approver.approvalRemark ? approver.approvalRemark : null}
                        userInfo={approver}
                        key={`approver-${idx}`}
                      />
                    );
                  })}

                  {approvers.length <= 1 || <Divider />}
                </>
              }
            />
            {cancelApprovalChildren}
          </TabPanel>
          <TabPanel value={tab} index={1}>
            {exchangeOfflineDetail.downloadHistory.length > 0 ? (
              exchangeOfflineDetail.downloadHistory.map((v, index) => {
                const fileName = exchangeOfflineDetail.fileDetails.find(
                  (file) => file.fileUuid === v.fileUuid
                )?.fileName;
                return (
                  <DownloadHistoryInfo
                    key={v.approvalNo + index}
                    fileName={fileName || ''}
                    updatedTsStr={toLocalDateTimeLangStr(
                      v.updatedTs,
                      getFasooUiComponentLanguage()
                    )}
                    userName={v.userName}
                    userId={v.userId}
                    userIpText={v.userIpText}
                    action={v.action}
                    adminRemark={null}
                  />
                );
              })
            ) : (
              <div className={classNames(`download-history-root`)}>
                <div style={{ height: '32px' }} />
                <FileDragDropIcon />
                <div style={{ height: '32px' }} />
                <Typography variant="p" color="secondary">
                  {t('userApproval:message.noFileHistory')}
                </Typography>
              </div>
            )}
          </TabPanel>
        </TabBox>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={exchangeOfflineDetail.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}

      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={exchangeOfflineDetail}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default ExchangeOfflineDetail;
